import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { Select, Input, Checkbox, Tag, Empty, Flex } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import styles from "./ExperienceSelector.module.css"
import { Text } from "ui/Typography"

const ExperienceSelector = ({ value = [], onChange, experiences = [] }) => {
  const normalizedValue = useMemo(() => value.map(String), [value])
  const [searchText, setSearchText] = useState("")

  const normalizedSearch = searchText.trim().toLowerCase()

  const filteredExperiences = useMemo(() => {
    if (!normalizedSearch) return experiences
    return experiences.filter((exp) => (exp.name || "").toLowerCase().includes(normalizedSearch))
  }, [experiences, normalizedSearch])

  const filteredIds = useMemo(() => filteredExperiences.map((exp) => String(exp.id)), [filteredExperiences])

  const areAllFilteredSelected = useMemo(() => {
    return filteredIds.length > 0 && filteredIds.every((id) => normalizedValue.includes(id))
  }, [filteredIds, normalizedValue])

  const handleSelectAll = (checked) => {
    if (checked) {
      const newValue = [...new Set([...normalizedValue, ...filteredIds])]
      onChange(newValue)
    } else {
      // Unselect only the currently filtered experiences, keep the rest
      const newValue = normalizedValue.filter((id) => !filteredIds.includes(id))
      onChange(newValue)
    }
  }

  const handleExperienceToggle = (expId, checked) => {
    const expIdStr = String(expId)
    let newValue
    if (checked) {
      newValue = [...normalizedValue, expIdStr]
    } else {
      newValue = normalizedValue.filter((id) => id !== expIdStr)
    }
    onChange(newValue)
  }

  const dropdownRender = (menu) => (
    <section
      className={styles.experienceDropdown}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={`${styles.searchContainer} p-2 border-b`}>
        <Input
          allowClear
          placeholder="Search experiences..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </div>
      <div className={`${styles.selectAll} p-2 border-b`}>
        <Checkbox
          checked={areAllFilteredSelected}
          // indeterminate={
          //   !areAllFilteredSelected &&
          //   filteredIds.some(id => normalizedValue.includes(id))
          // }
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          Select All
        </Checkbox>
      </div>
      <div
        className={styles.experienceList}
        style={{ maxHeight: "250px" }}
      >
        {filteredExperiences.length > 0 ? (
          filteredExperiences.map((exp) => {
            const expIdStr = String(exp.id)
            const isSelected = normalizedValue.includes(expIdStr)

            // Determine status label based on experience status
            let statusLabel = "Draft"
            if (exp.status === "published") {
              statusLabel = "Published"
            } else if (exp.status === "unpublished") {
              statusLabel = "Unpublished"
            }
            return (
              <button
                type="button"
                key={expIdStr}
                className={`${styles.experienceItem} p-2 hover:bg-gray-50`}
                aria-pressed={isSelected}
                style={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer', border: 'none', background: 'transparent', textAlign: 'left' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleExperienceToggle(expIdStr, !isSelected);
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => e.stopPropagation()}
                  style={{ pointerEvents: 'none' }}
                />
                <Flex align="center" wrap style={{ marginLeft: 8, flex: 1 }}>
                  <Text size="caption">
                    {exp.name}
                    <Text
                      size="caption"
                      color={exp.status === "published" ? "green" : "gray"}
                      fw="500"
                      style={{
                        marginLeft: 6,
                        padding: "0 4px"
                      }}
                    >
                      ({statusLabel})
                    </Text>
                  </Text>
                </Flex>
              </button>
            )
          })
        ) : (
          <div className="p-2 text-gray-500">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No experiences found" />
          </div>
        )}
      </div>
    </section>
  )

  const options = experiences.map((exp) => ({ label: exp.name, value: String(exp.id) }))
  return (
    <Select
      mode="multiple"
      placeholder="Select Experience"
      value={normalizedValue}
      showSearch={false}
      onChange={(vals) => onChange(vals.map(String))}
      dropdownRender={dropdownRender}
      options={options}
      className={styles.experienceSelect}
      tagRender={(props) => tagRenderExperience({ ...props, experiences, styles })}
      // Keep dropdown open if it was open even when last item removed (prevent flicker)
      onDropdownVisibleChange={(open) => {
        if (!open && searchText) {
          setTimeout(() => {
            const el = document.querySelector(".experience-dropdown")
            if (el) {
              // Trigger synthetic click to reopen only if select is not focused
            }
          }, 0)
        }
      }}
    />
  )

  // Move tagRender outside the component
  function tagRenderExperience({ value: expId, onClose, experiences, styles }) {
    const exp = experiences.find((e) => String(e.id) === String(expId))
    if (!exp) return null
    let statusLabel = "Draft"
    if (exp.status === "published") statusLabel = "Published"
    else if (exp.status === "unpublished") statusLabel = "Unpublished"
    const statusColor = exp.status === "published" ? "green" : "gray"
    return (
      <Tag closable onClose={onClose} className={`align-center ${styles.selectedTag}`}>
        <Text size="caption">
          {exp.name}
          <Text size="caption" className="pl-xs" fw="500" color={statusColor}>
            ({statusLabel})
          </Text>
        </Text>
      </Tag>
    )
  }
}

// Custom compare: shallow compare value array contents & experiences length + ids
const areEqual = (prevProps, nextProps) => {
  const prevVals = prevProps?.value?.map(String)
  const nextVals = nextProps?.value?.map(String)
  if (prevVals?.length !== nextVals?.length) return false
  for (let i = 0; i < prevVals?.length; i++) {
    if (prevVals[i] !== nextVals[i]) return false
  }
  const prevExps = prevProps?.experiences
  const nextExps = nextProps?.experiences
  if (prevExps?.length !== nextExps?.length) return false
  for (let i = 0; i < prevExps.length; i++) {
    if (String(prevExps[i].id) !== String(nextExps[i].id)) return false
    if (prevExps[i].name !== nextExps[i].name) return false
    if (prevExps[i].status !== nextExps[i].status) return false
  }
  return true
}


ExperienceSelector.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  experiences: PropTypes.array,
}

export default React.memo(ExperienceSelector, areEqual)
